import DropDown from "views/DropDown";
import React from "react";
import filenameToLabel from "lib/filenameToLabel";
import PropTypes from "prop-types";
import LoadObject from "data/LoadObject";
import ITTestHistoryStore, {
    isError,
    isSuccess,
} from "data/dev/ITTestHistoryStore";
import classnames from "classnames";
import Dispatcher from "Dispatcher";
import DevelopmentActions from "data/dev/DevelopmentActions";

const removePrefix = (str, prefix) => (
    str.indexOf(prefix) === 0
        ? str.substr(prefix.length)
        : str
);

class ITTestNavItem extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            text: null,
        };
        this.onClear = this.onClear.bind(this);
        this.doKeyDown = this.doKeyDown.bind(this);
        this.onHide = this.onHide.bind(this);
    }

    onClear() {
        Dispatcher.dispatch({
            type: DevelopmentActions.RESET_IT_TEST_STATUS,
        });
    };

    doKeyDown(e) {
        const k = e.key;
        if (this.resetTimeout != null) {
            clearTimeout(this.resetTimeout);
            this.resetTimeout = null;
        }
        // eslint-disable-next-line no-settimeout
        this.resetTimeout = setTimeout(() => {
            this.setState({
                text: null,
            });
        }, 5000);
        if (k === "Escape") {
            // this is bad form, but we need synchronicity, and it'll be close
            // enough for a dev-only thing that doesn't have to be perfect.
            const hadText = !!this.state.text;
            this.setState({
                text: null
            });
            if (!hadText) return;
        } else if (k === "Backspace") {
            this.setState(s => ({
                text: s.text == null ? null : s.text.substr(0, s.text.length - 1),
            }));
        } else if (k.length === 1) {
            this.setState(s => ({
                text: s.text == null ? k : (s.text + k),
            }));
        } else {
            return;
        }
        e.stopPropagation();
        e.preventDefault();
    };

    onHide() {
        this.setState({
            text: null,
        });
    }

    render() {
        const {itTestLO, history} = this.props;
        let {activeTest} = this.props;
        const { text: filterText } = this.state;
        if (itTestLO.isPending()) {
            return <li className="navbar-text">Loading...</li>;
        }
        if (itTestLO.hasError()) {
            return <li className="navbar-text"
                       title="There was an error loading the IT test manifest.">Error!</li>;
        }
        const {root, tests: allTests} = itTestLO.getRequiredValue();
        let tests;
        if (filterText) {
            const textToWords = text => text
                .replace(/([^A-Z])([A-Z])/g, "$1 $2")
                .toLowerCase()
                .split(" ")
                .map(it => it.trim())
                .filter(it => it.length);
            let catWords = null;
            let nameText = filterText;
            const slashIdx = filterText.indexOf("/");
            if (slashIdx >= 0) {
                catWords = textToWords(filterText.substr(0, slashIdx));
                nameText = filterText.substr(slashIdx + 1);
            }
            const words = textToWords(nameText);
            const isMatch = (words, str) => {
                const lc = str.toLowerCase();
                return words.every(w => lc.indexOf(w) >= 0);
            };
            tests = {};
            for (const cat of Object.keys(allTests)) {
                let ts;
                if (catWords) {
                    if (!isMatch(catWords, cat)) continue;
                    ts = allTests[cat].filter(t =>
                        isMatch(words, t));
                } else if (isMatch(words, cat)) {
                    ts = allTests[cat];
                } else {
                    ts = allTests[cat].filter(t =>
                        isMatch(words, cat + "/" + t));
                }
                if (ts.length > 0) {
                    tests[cat] = ts;
                }
            }
        } else {
            tests = allTests;
        }
        if (activeTest) {
            if (window.location.origin) {
                activeTest = removePrefix(
                    activeTest,
                    window.location.origin + "/",
                );
            }
            activeTest = removePrefix(activeTest, root + "/");
        }
        const activeCats = Object.keys(tests).sort((a, b) => {
            const A = a.toUpperCase();
            const B = b.toUpperCase();
            if (A < B) return -1;
            if (A > B) return 1;
            return 0;
        });
        let label = activeTest || "IT Tests";
        const maxLen = 30;
        if (label.length > maxLen) {
            const parts = label.split("/");
            label = parts.map((s, i) =>
                i === parts.length - 1 ? s : s.substring(0, 1)).join("/");
        }
        if (label.length > maxLen) {
            const parts = label.split("/");
            const last = parts.pop();
            const len = last.length;
            const toCull = Math.min(label.length - maxLen, len - 6);
            if (toCull > 3) {
                parts.push(last.substring(0, (len - toCull) / 2 - 4)
                    + "..."
                    + last.substring(len - 4 - (len - toCull) / 2));
                label = parts.join("/");
            }
        }
        return <DropDown
            label={label}
            align="right"
            onKeyDown={this.doKeyDown}
            onHide={this.onHide}
        >
            <li className="dropdown-header">
                {filterText
                    ? <React.Fragment>
                        Filter: <code>{filterText}</code>
                    </React.Fragment>
                    : <em>Start typing to filter...</em>
                }
            </li>
            {filterText && activeCats.length === 0 && <React.Fragment>
                <li><a>Zero tests match the filter.</a></li>
                <li role="separator" className="divider" />
            </React.Fragment>}
            {activeCats.map(k =>
                <React.Fragment key={k}>
                    <li className="dropdown-header" title={k}>
                        <code>{filenameToLabel(k)}</code>
                    </li>
                    {tests[k].map(t => {
                            const fullPath = `${root}/${k}/${t}`;
                            return <li key={t} title={`${k}/${t}`}
                                       className={classnames("IT-item", {
                                           active: `${k}/${t}` === activeTest,
                                           "IT-item-success": isSuccess(history, fullPath),
                                           "IT-item-error": isError(history, fullPath),
                                       })}>
                                <a href={`?IT=${fullPath}`}>{filenameToLabel(t)}</a>
                            </li>;
                        },
                    )}
                    <li role="separator" className="divider" />
                </React.Fragment>,
            )}
            <li><a onClick={this.onClear}>Clear History</a></li>
            <li><a href="test/IT-runner.html" target="_blank">Runner UI</a></li>
            <li><a href="test/IT.html" target="_blank">Setup Instructions</a>
            </li>
        </DropDown>;
    }
}

ITTestNavItem.propTypes = {
    activeTest: PropTypes.string,
    itTestLO: PropTypes.instanceOf(LoadObject).isRequired,
    history: ITTestHistoryStore.constructor.stateTypes, // this is questionable.
};

export default ITTestNavItem;



// WEBPACK FOOTER //
// ./app/views/dev/ITTestNavItem.js